"use client"

import { useEffect, useRef, useState, useCallback } from "react"
import { useTranslations } from "next-intl"
import {
  IconPhonePause,
  IconPlayerPlay,
  IconMicrophone,
  IconMicrophoneOff,
  IconArrowsTransferDown,
  IconPhoneOff,
  IconLoader2,
  IconCheck,
  IconX,
  IconNote,
} from "@tabler/icons-react"

import { Button } from "@/components/ui/button"
import { useTelephony } from "@/components/telephony/telephony-provider"
import { MidCallNotesPanel } from "@/components/telephony/mid-call-notes-panel"
import { useMidCallNotesStore } from "@/stores/mid-call-notes-store"

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
}

export function InCallControls() {
  const t = useTranslations("telephony")
  const tc = useTranslations("common")
  const ctx = useTelephony()
  const [elapsed, setElapsed] = useState(0)
  const [holdLoading, setHoldLoading] = useState(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  if (!ctx) return null

  const {
    callState,
    incomingNumber,
    isMuted,
    isHeld,
    toggleMute,
    hold,
    unhold,
    onTransfer,
    hangup,
    transferState,
    completeAttendedTransfer,
    cancelAttendedTransfer,
  } = ctx

  const { isOpen: notesOpen, togglePanel: toggleNotes } = useMidCallNotesStore()

  const visible =
    callState === "connecting" ||
    callState === "in-call" ||
    callState === "on-hold" ||
    callState === "consulting" ||
    callState === "transferring"

  // Timer: start counting when call becomes active, reset when idle
  useEffect(() => {
    if (callState === "in-call" || callState === "on-hold" || callState === "consulting") {
      if (!timerRef.current) {
        timerRef.current = setInterval(() => {
          setElapsed((prev) => prev + 1)
        }, 1000)
      }
    } else if (callState === "idle" || callState === "ringing") {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      setElapsed(0)
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [callState])

  const handleHoldToggle = useCallback(async () => {
    if (holdLoading) return
    setHoldLoading(true)
    try {
      if (isHeld) {
        await unhold()
      } else {
        await hold()
      }
    } finally {
      setHoldLoading(false)
    }
  }, [holdLoading, isHeld, hold, unhold])

  if (!visible) return null

  // Determine state label
  const isTransferring = transferState !== null
  let stateLabel = t("inCallControls.inCall")
  if (callState === "connecting") stateLabel = t("inCallControls.calling")
  else if (callState === "on-hold") stateLabel = t("inCallControls.onHold")
  else if (transferState?.status === "calling") stateLabel = t("inCallControls.callingTarget", { target: transferState.targetName })
  else if (transferState?.status === "consulting") stateLabel = t("inCallControls.consultingWith", { target: transferState.targetName })
  else if (transferState?.status === "completing") stateLabel = t("inCallControls.transferringTo", { target: transferState.targetName })

  // Consulting state: show complete/cancel buttons instead of normal controls
  if (transferState?.status === "consulting") {
    return (
      <div className="fixed bottom-6 right-6 z-50 w-80 rounded-xl border bg-background p-4 shadow-lg">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">{stateLabel}</p>
              <p className="text-sm font-medium">
                {incomingNumber || t("inCallControls.activeCall")}
              </p>
            </div>
            <p className="text-xs tabular-nums text-muted-foreground">
              {formatElapsed(elapsed)}
            </p>
          </div>

          <div className="flex items-center justify-center gap-2">
            <Button
              size="sm"
              className="h-8 bg-green-600 hover:bg-green-700 text-white"
              onClick={completeAttendedTransfer}
            >
              <IconCheck className="h-4 w-4 mr-1" />
              {t("inCallControls.completeTransfer")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              onClick={cancelAttendedTransfer}
            >
              <IconX className="h-4 w-4 mr-1" />
              {tc("cancel")}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Transferring/calling state: show spinner
  if (transferState?.status === "completing" || transferState?.status === "calling") {
    return (
      <div className="fixed bottom-6 right-6 z-50 w-80 rounded-xl border bg-background p-4 shadow-lg">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">{stateLabel}</p>
              <p className="text-sm font-medium">
                {incomingNumber || t("inCallControls.activeCall")}
              </p>
            </div>
            <IconLoader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
          {transferState?.status === "calling" && (
            <div className="flex items-center justify-center">
              <Button
                size="sm"
                variant="outline"
                className="h-8"
                onClick={cancelAttendedTransfer}
              >
                <IconX className="h-4 w-4 mr-1" />
                {tc("cancel")}
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 rounded-xl border bg-background p-4 shadow-lg">
      <MidCallNotesPanel />
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">{stateLabel}</p>
            <p className="text-sm font-medium">
              {incomingNumber || t("inCallControls.activeCall")}
            </p>
          </div>
          {(callState === "in-call" || callState === "on-hold") && (
            <p className="text-xs tabular-nums text-muted-foreground">
              {formatElapsed(elapsed)}
            </p>
          )}
        </div>

        <div className="flex items-center justify-center gap-2">
          {/* Hold / Resume */}
          <Button
            variant={isHeld ? "default" : "outline"}
            size="icon"
            className={`h-9 w-9 ${isHeld ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}`}
            onClick={handleHoldToggle}
            disabled={callState === "connecting" || holdLoading || isTransferring}
          >
            {isHeld ? (
              <IconPlayerPlay className="h-4 w-4" />
            ) : (
              <IconPhonePause className="h-4 w-4" />
            )}
          </Button>

          {/* Mute */}
          <Button
            variant={isMuted ? "default" : "outline"}
            size="icon"
            className={`h-9 w-9 ${isMuted ? "bg-red-500 hover:bg-red-600 text-white" : ""}`}
            onClick={toggleMute}
          >
            {isMuted ? (
              <IconMicrophoneOff className="h-4 w-4" />
            ) : (
              <IconMicrophone className="h-4 w-4" />
            )}
          </Button>

          {/* Transfer */}
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={onTransfer}
            disabled={callState === "connecting"}
          >
            <IconArrowsTransferDown className="h-4 w-4" />
          </Button>

          {/* Notes */}
          <Button
            variant={notesOpen ? "default" : "outline"}
            size="icon"
            className={`h-9 w-9 ${notesOpen ? "bg-blue-500 hover:bg-blue-600 text-white" : ""}`}
            onClick={toggleNotes}
          >
            <IconNote className="h-4 w-4" />
          </Button>

          {/* Hang Up */}
          <Button
            variant="destructive"
            size="icon"
            className="h-9 w-9"
            onClick={hangup}
          >
            <IconPhoneOff className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
